import type { Dispatch, SetStateAction } from "react";
import { PanelRight } from "lucide-react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import { Button } from "@/components/ui/button";
import { CountBadge } from "@/components/semantic-badges";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SegmentedControl } from "@/components/ui/segmented-control";
import {
  PageCommentInput,
  ThreadItem,
} from "@/features/rev-collab/rev-collab-session-helpers";
import type {
  CommentRecord,
  MentionableUser,
  StatusFilter,
  ThreadRecord,
} from "@/features/rev-collab/rev-collab-types";
import { cn } from "@/lib/utils";
import { useCanEdit } from "@/lib/capabilities";

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
] as const satisfies ReadonlyArray<{ value: StatusFilter; label: string }>;

interface RevCollabThreadPanelProps {
  isMobile: boolean;
  statusFilter: StatusFilter;
  setStatusFilter: Dispatch<SetStateAction<StatusFilter>>;
  threads: ThreadRecord[] | undefined;
  selectedThreadId: Id<"revCollabThreads"> | null;
  comments: CommentRecord[] | undefined;
  userId: string;
  mentionableUsers: MentionableUser[] | undefined;
  commentBody: string;
  setCommentBody: Dispatch<SetStateAction<string>>;
  selectedMentions: Set<string>;
  setSelectedMentions: Dispatch<SetStateAction<Set<string>>>;
  onSelectThread: (
    threadId: Id<"revCollabThreads">,
    annotationId?: Id<"revCollabAnnotations"> | null
  ) => Promise<void>;
  onSubmitComment: () => Promise<void>;
  onDeleteComment: (args: { commentId: Id<"revCollabComments"> }) => Promise<void | null>;
  onSetThreadStatus: (args: {
    threadId: Id<"revCollabThreads">;
    status: "open" | "resolved";
  }) => Promise<void | null>;
  onZoomToAnnotation: (annotationId: Id<"revCollabAnnotations">) => void;
  onClose: () => void;
  onMinimize: () => void;
  onCreatePageComment: (body: string, mentionIds: string[]) => Promise<void>;
}

export function RevCollabThreadPanel({
  isMobile,
  statusFilter,
  setStatusFilter,
  threads,
  selectedThreadId,
  comments,
  userId,
  mentionableUsers,
  commentBody,
  setCommentBody,
  selectedMentions,
  setSelectedMentions,
  onSelectThread,
  onSubmitComment,
  onDeleteComment,
  onSetThreadStatus,
  onZoomToAnnotation,
  onClose,
  onMinimize,
  onCreatePageComment,
}: RevCollabThreadPanelProps) {
  const canEdit = useCanEdit();
  return (
    <aside
      className={cn(
        "@container absolute bottom-4 right-4 top-4 z-20 w-[380px]",
        isMobile ? "left-4 w-auto" : ""
      )}
    >
      <Card className="flex h-full flex-col bg-background/95 shadow-sm backdrop-blur">
        <CardHeader className="space-y-3 pb-3">
          {/* Sized by the panel, not the viewport — query the container. */}
          <div className="flex flex-col gap-2 @[22rem]:flex-row @[22rem]:items-center @[22rem]:justify-between">
            <CardTitle className="text-base">Threads</CardTitle>
            <div className="flex items-center gap-1">
              <SegmentedControl
                aria-label="Filter threads by status"
                size="compact"
                value={statusFilter}
                onValueChange={setStatusFilter}
                options={STATUS_FILTER_OPTIONS}
              />
              {isMobile ? (
                <Button type="button" size="compact" variant="ghost" onClick={onClose}>
                  Close
                </Button>
              ) : (
                <Button type="button" size="compact" variant="ghost" onClick={onMinimize}>
                  Minimize
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col gap-3 p-3 pt-0">
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pe-1">
            {(threads ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No threads on this page.</p>
            ) : (
              (threads ?? []).map((thread) => (
                (() => {
                  const annotationId = thread.annotationId;
                  return (
                    <ThreadItem
                      key={thread._id}
                      thread={thread}
                      isSelected={selectedThreadId === thread._id}
                      comments={selectedThreadId === thread._id ? (comments ?? []) : []}
                      userId={userId}
                      mentionableUsers={mentionableUsers ?? []}
                      onSelect={() => onSelectThread(thread._id, annotationId)}
                      onSubmitComment={onSubmitComment}
                      onDeleteComment={onDeleteComment}
                      onSetStatus={(status) => onSetThreadStatus({ threadId: thread._id, status })}
                      onZoomToAnnotation={
                        annotationId != null ? () => onZoomToAnnotation(annotationId) : undefined
                      }
                      commentBody={commentBody}
                      setCommentBody={setCommentBody}
                      selectedMentions={selectedMentions}
                      setSelectedMentions={setSelectedMentions}
                    />
                  );
                })()
              ))
            )}
          </div>

          {canEdit ? (
            <div className="border-t pt-3">
              <p className="mb-2 text-xs font-medium text-muted-foreground">Add page comment</p>
              <PageCommentInput
                mentionableUsers={mentionableUsers ?? []}
                onSubmit={onCreatePageComment}
              />
            </div>
          ) : null}
        </CardContent>
      </Card>
    </aside>
  );
}

export function RevCollabThreadToggle({
  threadCount,
  onOpen,
}: {
  threadCount: number;
  onOpen: () => void;
}) {
  return (
    <div className="absolute right-4 top-4 z-20">
      <Button
        type="button"
        variant="outline"
        size="lg"
        className="rounded-full border-border/70 bg-background/95 px-4 shadow-sm backdrop-blur"
        onClick={onOpen}
      >
        <PanelRight />
        Threads
        <CountBadge value={threadCount} label="threads" />
      </Button>
    </div>
  );
}
